import React from 'react';

// Styles & Assets
import styles from './AddPollDialog.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useEffect, useState } from 'react';
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

// Components
import CheckboxButton from '../../UI/CheckboxButton/CheckboxButton';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import MultiSelectEnvironment from '../MultiSelectEnvironment/MultiSelectEnvironment';

// Interfaces
import { LinkedInquiry, Poll } from '../../../interfaces/Poll';
import { Case, Patient } from '../../../interfaces/Patient';

// Language
import { useTranslation } from 'react-i18next';

const AddPollDialog: React.FC<{
    showModal: React.Dispatch<React.SetStateAction<boolean>>
    patientID: Patient['id']
    caseID: Case['id']
}> = ({ showModal, patientID, caseID }) => {

    const { t } = useTranslation();

    const { isLoading: loadingInquiries, error: errorInquiries, sendRequest: getInquiries } = useHttp<Poll[]>();
    const { isLoading: loadingLinkedInquiries, error: errorLinkedInquiries, sendRequest: getLinkedInquiries } = useHttp<LinkedInquiry[]>();
    const { sendRequest } = useHttp();

    const [polls, setPolls] = useState<Poll[]>([]);

    const [environmentsFiltered, setEnvironmentsFiltered] = useState<number[]>([]);

    const [checkList, setCheckList] = useState<number[]>([]);
    const toast = useToast();

    useEffect(() => {
        getInquiries({
            url: getEnvironmentApiUrl() + 'inquiry/all',
            method: 'POST',
            body: {
                params: {},
                filter: {
                    is_profesional_inquiry: false,
                    enabled: true,
                    modified: false,
                    deleted: false,
                }
            },
        }, (data) => setPolls(data));
    }, [getInquiries]);

    useEffect(() => {
        if (polls.length === 0 || checkList.length > 0) return;

        const handleSetLinkedInquiries = (data: LinkedInquiry[]) => {
            setCheckList(data.map(inquiry => inquiry.inquiry_id));
        };

        getLinkedInquiries({
            url: getEnvironmentApiUrl() + 'patient_inquiry/all',
            method: 'POST',
            body: {
                params: {},
                filter: {
                    case_id: caseID, end_date: null
                }
            }
        }, handleSetLinkedInquiries);

    }, [checkList.length, getLinkedInquiries, caseID, polls]);

    const handleChangePollCheck = (id: number | undefined) => {

        const inquiry = polls.find(p => p.id === id);

        const inquiryID = inquiry?.id;
        if (inquiry === undefined || inquiryID === undefined || id === undefined) return;

        const link = !checkList.some(inquiryID => inquiryID === id);

        const bodyLink = [{
            patient_id: patientID,
            case_id: caseID,
            inquiry_id: inquiry.id,
            frecuency: inquiry.frecuency,
            semanal_frequency: inquiry.semanal_frequency,
            monthly_frequency_day: inquiry.monthly_frequency_day,
            frequency_duration: inquiry.frequency_duration,
            modifed_frequency: false,
            modifed_reminder: false,
            reminder_time: inquiry.reminder_time,
            start_date: new Date().toISOString(),
            end_date: null,
            assigned_by_preconfig: false
        }];

        const bodyUnlink = [caseID];

        sendRequest({
            url: getEnvironmentApiUrl() + (link ? 'patient_inquiry/multiple' : `inquiry/${inquiryID}/unlink_cases`),
            method: 'POST',
            body: link ? bodyLink : bodyUnlink,
        }, () => {
            setCheckList(oldChecklist => link ?
                [...oldChecklist, inquiryID] :
                oldChecklist.filter(id => id !== inquiryID)
            );
            toast.success(<p><b>{inquiry.name}</b> {link ? t('was_assigned_correctly') : t('was_unassigned_correctly')}</p>);
        }, () => {
            toast.error(<p><b>{inquiry.name}</b> {t('was_not_assigned_correctly')}</p>);
        });

    };

    const handleSetProfileIDs = (profileIDs: number[]) => {
        setEnvironmentsFiltered(profileIDs)
    }

    const filteredPolls = polls.filter(p => environmentsFiltered.length === 0 || p.profile_ids?.length === 0 ||
        p.profile_ids === null || p.profile_ids === undefined || p.profile_ids.some(pID => environmentsFiltered.includes(pID))
    );

    return (
        <div className='dialog'>
            <h1 className='dialogTitle' style={{ marginBottom: '24px' }}>
                {t('assign_poll')}
            </h1>
            <MultiSelectEnvironment
                selectedProfileIDs={environmentsFiltered}
                handleSetProfileIDs={handleSetProfileIDs}
            />
            <ErrorSpinnerWrapper
                loading={loadingInquiries || loadingLinkedInquiries}
                error={errorInquiries || errorLinkedInquiries}
            >
                <div className={styles.pollsContainer}>
                    {filteredPolls.map(poll =>
                        <CheckboxButton
                            key={poll.id}
                            label={poll.name ?? ''}
                            checked={checkList.some(id => id === poll.id)}
                            onClick={() => handleChangePollCheck(poll.id)}
                        />
                    )}
                </div>
            </ErrorSpinnerWrapper>
            <div className={styles.buttonContainer}>
                <button className='button' onClick={() => showModal(false)}>
                    {t('close')}
                </button>
            </div>
        </div>
    );
};

export default AddPollDialog;